import React, { useState, useContext } from "react";
import { toast } from "react-toastify";
import { showSuccess, showError } from "../../utils/toast";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { RiCloseCircleFill } from "react-icons/ri";
import PropTypes from "prop-types";
import { styled } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import EditUserForm from "./EditUserForm";
import ButtonUI from "../material-ui/ButtonUI";
import { AuthContext } from "../../context/Auth/AuthContext";
import editUser from "../../api/editUser";
import baseUrl from "../../config/baseUrl";
import { EditActionButton } from "../common/ActionIconButton";
import EditIcon from "@mui/icons-material/Edit";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}));

const BootstrapDialogTitle = (props) => {
  const { children, onClose, ...other } = props;

  return (
    <DialogTitle sx={{ m: 0, p: 2 }} {...other}>
      {children}
      {onClose ? (
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <RiCloseCircleFill />
        </IconButton>
      ) : null}
    </DialogTitle>
  );
};

BootstrapDialogTitle.propTypes = {
  children: PropTypes.node,
  onClose: PropTypes.func.isRequired,
};

const editUserFormSchema = yup.object().shape({
  firstName: yup.string().required("First Name is required field"),
  lastName: yup.string().required("Last Name is required field"),
  role: yup.string().required("User Type is required field"),
  userRole: yup.array().when("role", {
    is: "0",
    then: yup.array().min(1, "Role is required when user type is 'Non Admin'"),
    otherwise: yup.array(),
  }),
});

const EditUserPopup = ({ setUserAddedToTrue, tableValues }) => {
  const [open, setOpen] = useState(false);
  const { token, user_id, is_admin } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [userRolesOptions, setUserRolesOptions] = useState([]);

  const {
    register,
    watch,
    handleSubmit,
    control,
    formState: { errors },
    setValue,
  } = useForm({
    resolver: yupResolver(editUserFormSchema),
  });

  // Fetch roles only when the dialog is first opened
  const fetchUserRoles = async () => {
    if (userRolesOptions.length > 0) return; // already loaded
    try {
      const response = await fetch(`${baseUrl}/api/get-user-roles`, {
        method: "GET",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      setUserRolesOptions(data?.data || []);
    } catch (error) {
      console.error("Error loading user roles", error);
    }
  };

  const handleClickOpen = () => {
    setOpen(true);
    fetchUserRoles();
  };

  const handleClose = () => {
    setOpen(false);
  };

  const onEditUserFormSubmit = async (editUserFormData) => {
    setLoading(true);
    const editUserFormResponse = await editUser({
      token: token,
      user_id: user_id,
      id: tableValues.id,
      first_name: editUserFormData.firstName,
      last_name: editUserFormData.lastName,
      is_admin: editUserFormData.role,
      status: editUserFormData.status,
      user_role: editUserFormData.userRole,
    });

    if (editUserFormResponse.code === 200) {
      setUserAddedToTrue();
      setOpen(false);
      setLoading(false);

      showSuccess("User is updated successfully");
    } else {
      setLoading(false);
      showError("Error while adding document");
    }
  };

  return (
    <div className="dialog-modal">
      <EditActionButton onClick={handleClickOpen} />
      <BootstrapDialog
        onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        className="custom-edit-modal"
        open={open}
      >
        <BootstrapDialogTitle
          id="customized-dialog-title"
          className="edit-doc-modal"
          onClose={handleClose}
        >
          <div className="popup-header-svg-icon">
            <EditIcon />
          </div>
          <div className="popup-header-title">Edit User</div>
        </BootstrapDialogTitle>
        <DialogContent dividers>
          <EditUserForm
            watch={watch}
            register={register}
            control={control}
            errors={errors}
            tableValues={tableValues}
            setValue={setValue}
            userRolesOptions={userRolesOptions}
          />
        </DialogContent>
        <DialogActions sx={{ paddingBottom: "15px !important" }}>
          <div className="popup_submit_btn">
            <ButtonUI
              loading={loading}
              variant="contained"
              buttonText="Submit"
              style={{
                width: "150px",
                height: "35px",
                backgroundColor: "#1a6b7c",
                boxShadow: "none",
                border: "1px solid #1a6b7c",
                fontWeight: "500",
                borderRadius: "4px",
                letterSpacing: "0.07rem",
                marginTop: "20px",
              }}
              handleSubmit={handleSubmit}
              onFormSubmit={onEditUserFormSubmit}
            />
          </div>
        </DialogActions>
      </BootstrapDialog>
    </div>
  );
};

export default EditUserPopup;
